import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './useUserProfile';

export interface Tag {
  id: string;
  user_id: string;
  name: string;
  color: string;
  created_at: string;
}

export function useTags() {
  const { user, isReady } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch tags with React Query
  const { data: tags = [], isLoading } = useQuery({
    queryKey: ['tags', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('tags')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error fetching tags:', error);
        return [];
      }
      
      return data as Tag[];
    },
    enabled: !!user && isReady,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000,
  });

  // Add tag mutation
  const addMutation = useMutation({
    mutationFn: async ({ name, color }: { name: string; color: string }) => {
      if (!user) throw new Error('Not authenticated');
      
      const { data, error } = await supabase
        .from('tags')
        .insert({ user_id: user.id, name, color })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', user?.id] });
      toast({
        title: 'Создано',
        description: 'Метка успешно добавлена',
      });
    },
    onError: (error) => {
      console.error('Error adding tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось создать метку',
        variant: 'destructive',
      });
    },
  });

  // Update tag mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Tag> }) => {
      const { error } = await supabase
        .from('tags')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', user?.id] });
      toast({
        title: 'Сохранено',
        description: 'Метка обновлена',
      });
    },
    onError: (error) => {
      console.error('Error updating tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось обновить метку',
        variant: 'destructive',
      });
    },
  });

  // Delete tag mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tags')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tags', user?.id] });
      toast({
        title: 'Удалено',
        description: 'Метка удалена',
      });
    },
    onError: (error) => {
      console.error('Error deleting tag:', error);
      toast({
        title: 'Ошибка',
        description: 'Не удалось удалить метку',
        variant: 'destructive',
      });
    },
  });

  // Wrapper functions for API compatibility
  const addTag = async (name: string, color: string) => {
    if (!user) return null;
    try {
      return await addMutation.mutateAsync({ name, color });
    } catch {
      return null;
    }
  };

  const updateTag = async (id: string, updates: Partial<Tag>) => {
    try {
      await updateMutation.mutateAsync({ id, updates });
      return true;
    } catch {
      return false;
    }
  };

  const deleteTag = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      return true;
    } catch {
      return false;
    }
  };

  return {
    tags,
    loading: !isReady || isLoading,
    user,
    addTag,
    updateTag,
    deleteTag,
    refetch: () => queryClient.invalidateQueries({ queryKey: ['tags', user?.id] }),
  };
}
